import { BookOpenLight } from '@repo/go-icons';
import { Button as GOButton } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SaveIcon from 'src/shared/assets/icons/checkmark.svg';
import CloseIcon from 'src/shared/assets/icons/cross.svg';
import FullIcon from 'src/shared/assets/icons/full-icon.svg';
import SettingsIcon from 'src/shared/assets/icons/gear.svg';
import EditIcon from 'src/shared/assets/icons/pencil.svg';
import Retry from 'src/shared/assets/icons/retry-arrows.svg';
import { Button } from 'src/shared/components/button';
import { SwitchButtonGroup } from 'src/shared/components/switch-button-group';
import { useStore } from 'src/store';
import { PlanVersionType } from 'src/store/comparison/types';
import {
  GOPLAN_DRILLING_CARPET_GRAPH_EDIT,
  GOPLAN_DRILLING_CARPET_GRAPH_READ,
  GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT,
  GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT,
  GOPLAN_DRILLING_CARPET_WELL_CLUSTER_ACCESS_READ,
} from 'src/store/roles-store/roles';

import type { HeaderStore } from '../../pages/drilling-plan-chart/header.store';
import { PlanType } from '../drilling-chart/shared/plan-type';

import styles from './drilling-plan-header.module.scss';

interface Props {
  store: HeaderStore;
  onHistoryShow: VoidFunction;
  onSettingsOpen: VoidFunction;
  onFullScreen: VoidFunction;
  onRecalculateParameters: VoidFunction;
  onToggleLegend?: VoidFunction;
  additionalButtons?: ReactNode | ((props: { disabled: boolean }) => ReactNode);
  isLegendOpen?: boolean;
  isDisabled?: boolean;
  /** Disable any changes than can update state. For ex., editing/saving/publishing. */
  isChangesDisabled?: boolean;
}

export const DrillingPlanHeader = observer(function DrillingPlanHeader({
  store,
  additionalButtons,
  onHistoryShow,
  onSettingsOpen,
  onFullScreen,
  onRecalculateParameters,
  isDisabled,
  isChangesDisabled,
  onToggleLegend,
  isLegendOpen,
}: Props) {
  const { t } = useTranslation();
  const { rolesStore, planVersionsService } = useStore();

  const {
    init,
    isEditing,
    displayChartType,
    onDisplayChartTypeChange,
    isLoading,
    onCancel,
    onSave,
    onPublish,
    isEditModeLoading,
    editing,
    jobListStore,
    onEdit,
  } = store;

  const isPublishingAvailable = useMemo(() => {
    const draft = planVersionsService.currentDraft;

    if (!draft) {
      return false;
    }

    const parentVersion = planVersionsService.versionsList.find((ver) => ver.id === draft.data.parentVersionId);

    if (!parentVersion) {
      return false;
    }

    if (parentVersion.data.versionType === PlanVersionType.public) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PUBLIC_DRAFT]);
    }

    if (parentVersion.data.versionType === PlanVersionType.personal) {
      return rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_PUBLISH_PERSONAL_DRAFT]);
    }

    return false;
  }, [planVersionsService.currentDraft, planVersionsService.versionsList, rolesStore]);

  const switchButtonsItems = useMemo(() => {
    const items = [];

    if (rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_WELL_CLUSTER_ACCESS_READ])) {
      items.push({ key: PlanType.wells, title: t('drillingCarpet:Header.wellPad'), disabled: isEditing });
    }

    if (rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_READ, GOPLAN_DRILLING_CARPET_GRAPH_EDIT])) {
      items.push({ key: PlanType.rigs, title: t('drillingCarpet:Header.drillingRig') });
    }

    return items;
  }, [isEditing, rolesStore, t]);

  useEffect(init, [init]);

  return (
    <div className={styles.container}>
      <SwitchButtonGroup
        items={switchButtonsItems}
        defaultOptionKey={displayChartType}
        className={styles.switchContainer}
        buttonClassName={styles.switchButton}
        onChange={onDisplayChartTypeChange}
        disabled={isDisabled}
      />

      {isEditing && isPublishingAvailable && (
        <Button
          className={styles.editingControlsButton}
          variant="success"
          onClick={onPublish}
          disabled={isDisabled || isChangesDisabled || isLoading}
          isLoading={isLoading}
        >
          {t('Buttons.toPublish')}
        </Button>
      )}

      <div className={styles.controlsContainer}>
        {isEditing && (
          <Button
            variant="text"
            prefixIcon={<Retry className={styles.retryIcon} />}
            isLoading={isLoading}
            onClick={onRecalculateParameters}
          >
            <p className={styles.retryText}>{t('drillingCarpet:Header.recalculateParameters')}</p>
          </Button>
        )}

        {typeof additionalButtons === 'function'
          ? additionalButtons({
              disabled: isDisabled || isChangesDisabled || isEditModeLoading || !!jobListStore.currentImportVersionJob,
            })
          : additionalButtons}

        {isEditing ? (
          <div className={styles.editingControls}>
            <GOButton
              className={styles.editingControlsButton}
              variant="danger"
              icon={<CloseIcon />}
              onClick={onCancel}
              disabled={isDisabled || isChangesDisabled || isLoading}
              loading={isLoading}
            />

            <GOButton
              className={styles.editingControlsButton}
              variant="success"
              icon={<SaveIcon />}
              onClick={onSave}
              disabled={isDisabled || isChangesDisabled || isLoading}
              loading={isLoading}
            />
          </div>
        ) : (
          <>
            {store.isEditingBtnAvailable && (
              <GOButton
                style={{
                  width: 40,
                  height: 40,
                }}
                variant="secondary"
                onClick={onEdit}
                icon={<EditIcon />}
                className={clsx(styles.controlsButton, styles.controlsButtonEdit)}
                title={t('edit')}
                disabled={
                  isDisabled || isChangesDisabled || isEditModeLoading || !!jobListStore.currentImportVersionJob
                }
                loading={isEditModeLoading}
              />
            )}
            {/* Временно отключенный функционал */}
            {/* <ButtonWithTooltip
              onClick={onHistoryShow}
              icon={<HistoryIcon className={styles.controlsButtonIcon} />}
              className={styles.controlsButton}
              tooltipText={t('historyOfChange')}
              isDisabled={isDisabled}
            /> */}
          </>
        )}

        {onToggleLegend && (
          <GOButton
            style={{
              width: 40,
              height: 40,
            }}
            className={styles.legendButton}
            variant={isLegendOpen ? 'primary' : 'secondary'}
            title={t('drillingCarpet:Header.openLegend')}
            icon={<BookOpenLight />}
            onClick={onToggleLegend}
          />
        )}

        <GOButton
          onClick={onSettingsOpen}
          variant="secondary"
          icon={<SettingsIcon className={styles.controlsButtonIcon} />}
          className={styles.legendButton}
          title={t('displaySettings')}
          disabled={isDisabled}
        />

        <GOButton
          onClick={onFullScreen}
          variant="secondary"
          icon={<FullIcon className={styles.controlsButtonIcon} />}
          className={styles.legendButton}
          title={t('fullscreen')}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
});
